/**
 * Global XYZ tile basemap (satellite / aerial imagery) drawn BENEATH the chart,
 * the way Savvy Navvy uses a photo base: imagery underneath, with the navigation
 * data (marks, route, tides, AIS) drawing on top. One global source.
 *
 * Satellite is an OVERLAY (the satellite store toggles it), not a chart provider.
 * The free default (Esri World Imagery) needs no key. A premium keyed source
 * (MapTiler Satellite) reads its key from .env.local or the Settings field and
 * substitutes it into the URL here; the key is never committed.
 */
import { type Map as MaplibreMap } from 'maplibre-gl';

const SRC = 'tile-basemap';
const LYR = 'tile-basemap';

/** The id of the lowest non-background layer, so imagery sits beneath it. */
function bottomBeforeId(map: MaplibreMap): string | undefined {
  const layers = map.getStyle()?.layers ?? [];
  for (const l of layers) if (l.id !== 'background') return l.id;
  return undefined;
}

/** Effective premium key: a Settings entry overrides the .env.local key. */
export function satelliteKey(settingsKey: string): string {
  const s = settingsKey.trim();
  if (s) return s;
  const env = (import.meta.env as Record<string, string | undefined>).VITE_MAPTILER_KEY ?? '';
  return env.trim();
}

/** Which satellite imagery source the overlay uses. */
export type SatelliteSourceId = 'esri' | 'maptiler';

/** One selectable satellite imagery source for the overlay. */
export interface SatelliteSource {
  id: SatelliteSourceId;
  /** Display name in the selector. */
  name: string;
  /** One-line character under the name. */
  short: string;
  /** XYZ URL template; {key} is substituted at runtime (free sources omit it). */
  urlTemplate: string;
  tileSize: number;
  maxzoom: number;
  /** True if a premium key is required (the HD source); free sources leave it unset. */
  requiresKey?: boolean;
  attribution: string;
}

/**
 * The satellite imagery sources, in selector order. Free Esri World Imagery is
 * the default (no key, works out of the box, close to the Savvy Navvy look). The
 * premium MapTiler source needs a key (.env.local or Settings) for best quality.
 */
export const SATELLITE_SOURCES: SatelliteSource[] = [
  {
    id: 'esri',
    name: 'Satellite',
    short: 'Esri World Imagery — free, no key',
    urlTemplate:
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    tileSize: 256,
    maxzoom: 19,
    attribution:
      'Imagery © Esri — Source: Esri, Vantor, Earthstar Geographics, and the GIS User Community',
  },
  {
    id: 'maptiler',
    name: 'Satellite HD',
    short: 'MapTiler Satellite — needs a key',
    urlTemplate: 'https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key={key}',
    tileSize: 512,
    maxzoom: 20,
    requiresKey: true,
    attribution: '© MapTiler © Esri © Maxar',
  },
];

export function getSatelliteSource(id: SatelliteSourceId): SatelliteSource {
  return SATELLITE_SOURCES.find((s) => s.id === id) ?? SATELLITE_SOURCES[0];
}

/**
 * The source to actually use: the chosen one, unless it needs a key we don't
 * have, in which case fall back to the free Esri source so the overlay still
 * shows rather than going blank.
 */
export function resolveSatelliteSource(id: SatelliteSourceId, settingsKey: string): SatelliteSource {
  const chosen = getSatelliteSource(id);
  if (chosen.requiresKey && !satelliteKey(settingsKey)) return getSatelliteSource('esri');
  return chosen;
}

/** Substitute the key into a tile URL template ({key}); free sources are unchanged. */
export function buildTileUrl(template: string, key: string): string {
  return template.replace('{key}', key);
}

export interface ActiveTileBasemap {
  id: string;
  /** Final XYZ tile URL, key already substituted. */
  url: string;
  tileSize: number;
  maxzoom: number;
  attribution: string;
  /** Subtle dim/desaturate so navigation marks stay legible over bright imagery. */
  dim: boolean;
  /** Layer opacity 0..1 (the blend strength under a vector chart). Default 1. */
  opacity?: number;
}

let currentSig: string | null = null;

/** Show a tile basemap (replacing any current one), or clear it with null. */
export function setTileBasemap(map: MaplibreMap, base: ActiveTileBasemap | null): void {
  if (!base) {
    if (map.getLayer(LYR)) map.removeLayer(LYR);
    if (map.getSource(SRC)) map.removeSource(SRC);
    currentSig = null;
    return;
  }
  const brightnessMax = base.dim ? 0.82 : 1;
  const saturation = base.dim ? -0.15 : 0;
  const opacity = base.opacity == null ? 1 : Math.min(1, Math.max(0, base.opacity));
  const sig = `${base.id}|${base.url}|${base.tileSize}|${base.maxzoom}`;
  // Same source already up: only the dim/opacity settings can have changed, so
  // just update the paint rather than tearing the tiles down and refetching.
  if (currentSig === sig && map.getSource(SRC) && map.getLayer(LYR)) {
    map.setPaintProperty(LYR, 'raster-brightness-max', brightnessMax);
    map.setPaintProperty(LYR, 'raster-saturation', saturation);
    map.setPaintProperty(LYR, 'raster-opacity', opacity);
    return;
  }
  if (map.getLayer(LYR)) map.removeLayer(LYR);
  if (map.getSource(SRC)) map.removeSource(SRC);
  map.addSource(SRC, {
    type: 'raster',
    tiles: [base.url],
    tileSize: base.tileSize,
    maxzoom: base.maxzoom,
    attribution: base.attribution,
  });
  map.addLayer(
    {
      id: LYR,
      type: 'raster',
      source: SRC,
      paint: {
        'raster-fade-duration': 150,
        'raster-brightness-max': brightnessMax,
        'raster-saturation': saturation,
        'raster-opacity': opacity,
      },
    },
    bottomBeforeId(map),
  );
  currentSig = sig;
}
